import { useCallback } from 'react';
import { useSetRecoilState } from 'recoil';
import { busyIndicatorState } from '~/state/atoms/viewerAtoms';

/**
 * Hook for controlling the global busy indicator.
 *
 * @example
 * const { showBusy, hideBusy } = useBusyIndicator();
 *
 * const handleLongOperation = async () => {
 *     await showBusy('Processing data...');
 *     try {
 *         await doSomethingLong();
 *     } finally {
 *         hideBusy();
 *     }
 * };
 */
export default function useBusyIndicator() {
    const setBusyIndicator = useSetRecoilState(busyIndicatorState);

    const showBusy = useCallback(
        async (message: string) => {
            setBusyIndicator({ isOpen: true, message });
            // Yield to allow the UI to render the indicator
            await new Promise((resolve) => setTimeout(resolve, 50));
        },
        [setBusyIndicator]
    );

    const hideBusy = useCallback(() => {
        setBusyIndicator({ isOpen: false });
    }, [setBusyIndicator]);

    return { showBusy, hideBusy };
}
